import React, { useState, useMemo, useEffect } from 'react';
import { getWorkoutTypeName } from '../../utils/workoutColors';
import {
  formatDistance,
  getUnitPreference
} from '../../utils/unitConversions';
import RetestModal from '../RetestModal';
import GarminConnect from '../GarminConnect';
import WhoopConnect from '../WhoopConnect';
import OuraConnect from '../OuraConnect';
import CalendarView from '../Calendar/CalendarView';
import CreatePlanModal from './CreatePlanModal';
import WorkoutBuilder from '../Calendar/WorkoutBuilder';
import './TrainingPlanView.css';

// Helper to parse pace string or object to min/km
const parsePaceToMinPerKm = (paceData) => {
  if (!paceData) return null;

  if (typeof paceData === 'string') {
    // Format: "5:30" or "5:30/km" or "5:30 - 6:00/km" (range)
    // For ranges, use the faster pace (first value)
    const match = paceData.match(/(\d+):(\d+)/);
    if (match) {
      return parseInt(match[1]) + parseInt(match[2]) / 60;
    }
  } else if (typeof paceData === 'object' && paceData !== null) {
    // Object format varies by pace type:
    // - Easy: {pace: "5:30 - 6:00", pace_min: "5:30", pace_max: "6:00"}
    // - Marathon/Threshold: {pace: "4:50"}
    // - Interval: {pace: "4:00", pace_km: "4:00"}
    // - Repetition: {pace: "1:20" (400m time!), pace_km: "3:20" (per-km)}

    // First check for explicit min_per_km
    if (paceData.min_per_km) {
      return paceData.min_per_km;
    }

    // For repetition, use pace_km (the 'pace' field is 400m time, not per-km!)
    if (paceData.pace_km && typeof paceData.pace_km === 'string') {
      const match = paceData.pace_km.match(/(\d+):(\d+)/);
      if (match) {
        return parseInt(match[1]) + parseInt(match[2]) / 60;
      }
    }

    // For easy pace, use average of pace_min and pace_max
    if (paceData.pace_min && paceData.pace_max) {
      const minMatch = paceData.pace_min.match(/(\d+):(\d+)/);
      const maxMatch = paceData.pace_max.match(/(\d+):(\d+)/);
      if (minMatch && maxMatch) {
        const minPace = parseInt(minMatch[1]) + parseInt(minMatch[2]) / 60;
        const maxPace = parseInt(maxMatch[1]) + parseInt(maxMatch[2]) / 60;
        return (minPace + maxPace) / 2;
      }
    }

    // Fallback to pace field (works for marathon, threshold)
    if (paceData.pace && typeof paceData.pace === 'string') {
      const match = paceData.pace.match(/(\d+):(\d+)/);
      if (match) {
        return parseInt(match[1]) + parseInt(match[2]) / 60;
      }
    }
  }
  return null;
};

// Calculate weekly pace breakdown from structured workouts
// Uses actual distances for distance-based steps, estimates from time using VDOT paces
const calculatePaceBreakdown = (week, trainingPaces) => {
  if (!week?.workouts) return null;

  const breakdown = {
    easy: { distance: 0, time: 0 },
    marathon: { distance: 0, time: 0 },
    threshold: { distance: 0, time: 0 },
    interval: { distance: 0, time: 0 },
    repetition: { distance: 0, time: 0 },
  };

  week.workouts.forEach(workout => {
    if (!workout.steps || workout.steps.length === 0) return;

    workout.steps.forEach(step => {
      const pace = step.pace?.toLowerCase();
      if (!pace || !breakdown[pace]) return;

      if (step.duration_type === 'distance') {
        // Use actual distance (meters to km)
        breakdown[pace].distance += (step.duration_value || 0) / 1000;
      } else if (step.duration_type === 'time') {
        // Track time
        const minutes = (step.duration_value || 0) / 60;
        breakdown[pace].time += minutes;

        // Estimate distance from time using user's VDOT pace
        if (trainingPaces) {
          const minPerKm = parsePaceToMinPerKm(trainingPaces[pace]);
          if (minPerKm && minPerKm > 0) {
            breakdown[pace].distance += minutes / minPerKm;
          }
        }
      }
    });
  });

  return breakdown;
};

// Pace Breakdown Component
const PaceBreakdown = ({ week, unit, trainingPaces }) => {
  const breakdown = useMemo(() => calculatePaceBreakdown(week, trainingPaces), [week, trainingPaces]);

  if (!breakdown) return null;

  const paceColors = {
    easy: '#4ade80',      // Green
    marathon: '#60a5fa',   // Blue
    threshold: '#f59e0b',  // Amber
    interval: '#ef4444',   // Red
    repetition: '#8b5cf6', // Purple
  };

  const paceLabels = {
    easy: 'Easy',
    marathon: 'Marathon',
    threshold: 'Threshold',
    interval: 'Interval',
    repetition: 'Repetition',
  };

  // Calculate total distance
  const total = Object.values(breakdown).reduce((sum, p) => sum + p.distance, 0);

  // Only show if we have some data
  if (total <= 0) return null;

  // Filter to only show paces with actual distance
  const activePaces = Object.entries(breakdown).filter(([_, data]) => data.distance > 0);

  if (activePaces.length === 0) return null;

  return (
    <div className="pace-breakdown">
      <h4>Weekly Pace Distribution</h4>
      <div className="pace-bar">
        {activePaces.map(([pace, data]) => {
          const percent = (data.distance / total) * 100;
          return (
            <div
              key={pace}
              className="pace-segment"
              style={{
                width: `${percent}%`,
                backgroundColor: paceColors[pace],
              }}
              title={`${paceLabels[pace]}: ${formatDistance(data.distance, unit, 1)} ${unit}`}
            />
          );
        })}
      </div>
      <div className="pace-legend">
        {activePaces.map(([pace, data]) => (
          <div key={pace} className="pace-legend-item">
            <span
              className="pace-dot"
              style={{ backgroundColor: paceColors[pace] }}
            />
            <span className="pace-label">{paceLabels[pace]}</span>
            <span className="pace-value">
              {formatDistance(data.distance, unit, 1)} {unit}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TrainingPlanView = ({ trainingPlan, profile, onPlanUpdate }) => {
  const [currentPlan, setCurrentPlan] = useState(trainingPlan);
  const [retestModalOpen, setRetestModalOpen] = useState(false);
  const [selectedRetestWeek, setSelectedRetestWeek] = useState(null);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(null);
  const [showWeekDetails, setShowWeekDetails] = useState(false);
  const [createPlanModalOpen, setCreatePlanModalOpen] = useState(false);
  const [workoutBuilderOpen, setWorkoutBuilderOpen] = useState(false);
  const [workoutBuilderDate, setWorkoutBuilderDate] = useState(null);
  const [garminUserId, setGarminUserId] = useState(null);
  const [calendarRefreshTrigger, setCalendarRefreshTrigger] = useState(0);

  // Get Garmin user ID from localStorage
  useEffect(() => {
    const storedGarminId = localStorage.getItem('garmin_user_id');
    if (storedGarminId) {
      setGarminUserId(storedGarminId);
    }
  }, []);

  // Get user's preferred distance unit (profile setting takes priority, then localStorage)
  const profileUnits = profile?.preferred_units || profile?.preferredUnits;
  // Map 'miles' to 'mi' for the utility function
  const unit = profileUnits === 'miles' ? 'mi' : (profileUnits || getUnitPreference());

  // Sync with parent plan
  useEffect(() => {
    setCurrentPlan(trainingPlan);
  }, [trainingPlan]);

  // Format date to YYYY-MM-DD
  const formatDateString = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Get current week index (the actual current week based on today's date)
  const currentWeekIndex = useMemo(() => {
    if (!currentPlan?.weeks) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = formatDateString(today);

    for (let i = 0; i < currentPlan.weeks.length; i++) {
      const week = currentPlan.weeks[i];
      const workouts = week.workouts || [];
      if (workouts.length === 0) continue;

      const dates = workouts.map(w => w.date).sort();
      const minDate = dates[0];
      const maxDate = dates[dates.length - 1];

      if (todayStr >= minDate && todayStr <= maxDate) {
        return i;
      }
    }

    // If no current week found, return the week with next upcoming workout
    for (let i = 0; i < currentPlan.weeks.length; i++) {
      for (const workout of currentPlan.weeks[i].workouts || []) {
        if (workout.date >= todayStr) {
          return i;
        }
      }
    }

    return 0;
  }, [currentPlan]);

  // Set initial selected week to current week
  useEffect(() => {
    if (selectedWeekIndex === null && currentPlan?.weeks) {
      setSelectedWeekIndex(currentWeekIndex);
    }
  }, [currentWeekIndex, currentPlan, selectedWeekIndex]);

  // Get the displayed week data (either selected or current)
  const displayedWeekData = useMemo(() => {
    if (!currentPlan?.weeks) return null;
    const idx = selectedWeekIndex !== null ? selectedWeekIndex : currentWeekIndex;
    return currentPlan.weeks[idx] || currentPlan.weeks[0];
  }, [currentPlan, selectedWeekIndex, currentWeekIndex]);

  // Calculate total distance from pace breakdown (ensures header matches breakdown)
  const calculatedTotalKm = useMemo(() => {
    if (!displayedWeekData) return 0;
    const breakdown = calculatePaceBreakdown(displayedWeekData, currentPlan?.training_paces);
    if (!breakdown) return displayedWeekData.total_distance_km || 0;
    const total = Object.values(breakdown).reduce((sum, p) => sum + p.distance, 0);
    return total > 0 ? total : (displayedWeekData.total_distance_km || 0);
  }, [displayedWeekData, currentPlan?.training_paces]);

  // Get week date range
  const getWeekDateRange = (week) => {
    if (!week?.workouts?.length) return '';
    const dates = week.workouts.map(w => w.date).sort();
    const startDate = new Date(dates[0]);
    const endDate = new Date(dates[dates.length - 1]);
    const formatOptions = { day: 'numeric', month: 'short' };
    return `${startDate.toLocaleDateString('en-GB', formatOptions)} - ${endDate.toLocaleDateString('en-GB', formatOptions)}`;
  };

  // Navigation handlers
  const goToPreviousWeek = () => {
    if (selectedWeekIndex > 0) {
      setSelectedWeekIndex(selectedWeekIndex - 1);
    }
  };

  const goToNextWeek = () => {
    if (currentPlan?.weeks && selectedWeekIndex < currentPlan.weeks.length - 1) {
      setSelectedWeekIndex(selectedWeekIndex + 1);
    }
  };

  const goToCurrentWeek = () => {
    setSelectedWeekIndex(currentWeekIndex);
  };

  // Check if viewing current week
  const isViewingCurrentWeek = selectedWeekIndex === currentWeekIndex;

  // Format race name
  const formatRaceName = (race) => {
    const names = {
      '5k': '5K',
      '10k': '10K',
      'half_marathon': 'Half Marathon',
      'marathon': 'Marathon',
      'ultra_marathon': 'Ultra Marathon'
    };
    return names[race] || race;
  };

  // Format phase
  const formatPhase = (phase) => {
    const phases = {
      'PHASE_I': 'Base Building',
      'PHASE_II': 'Early Quality',
      'PHASE_III': 'Peak Training',
      'PHASE_IV': 'Taper'
    };
    return phases[phase] || phase;
  };

  // Retest handlers
  const handleOpenRetestModal = (weekNumber) => {
    setSelectedRetestWeek(weekNumber);
    setRetestModalOpen(true);
  };

  const handleCloseRetestModal = () => {
    setRetestModalOpen(false);
    setSelectedRetestWeek(null);
  };

  const handlePlanUpdate = (updatedPlan) => {
    setCurrentPlan(updatedPlan);
    if (onPlanUpdate) onPlanUpdate(updatedPlan);
    handleCloseRetestModal();
  };

  // Get day abbreviations for workout badges
  const dayAbbreviations = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (!currentPlan) {
    return <div className="training-plan-view">No training plan available</div>;
  }

  return (
    <div className="training-plan-view">
      <div className="training-plan-container">
        <div className="training-plan-layout">
          <div className="plan-main-column">
            {/* Week Summary with Navigation - Collapsible */}
            {displayedWeekData && (
              <div className={`week-summary-card ${displayedWeekData.is_retest_week ? 'retest' : ''}`}>
                <div className="week-summary-header-row">
                  <button
                    type="button"
                    className="week-summary-toggle"
                    onClick={() => setShowWeekDetails(!showWeekDetails)}
                  >
                    <span className={`week-toggle-arrow ${showWeekDetails ? 'expanded' : ''}`}>
                      {showWeekDetails ? '▼' : '▶'}
                    </span>
                    <div>
                      <h3>{formatRaceName(profile?.goalRace || profile?.goal_race)} Training Plan</h3>
                      <span className="week-summary-phase-inline">{formatPhase(displayedWeekData.phase)}</span>
                    </div>
                  </button>
                </div>

                {showWeekDetails && (
                  <div className="week-summary-content">
                    <div className="week-summary-header">
                      <div className="week-summary-info">
                        <span className="week-summary-dates">{getWeekDateRange(displayedWeekData)}</span>
                      </div>
                      <div className="week-summary-stats">
                        <div className="summary-stat">
                          <span className="summary-stat-value">
                            {formatDistance(calculatedTotalKm, unit, 0)}
                          </span>
                          <span className="summary-stat-label">{unit}</span>
                        </div>
                        <div className="summary-stat">
                          <span className="summary-stat-value">{displayedWeekData.quality_sessions || 0}</span>
                          <span className="summary-stat-label">quality</span>
                        </div>
                      </div>
                    </div>

                    {/* Weekly Pace Breakdown */}
                    <PaceBreakdown
                      week={displayedWeekData}
                      unit={unit}
                      trainingPaces={currentPlan?.training_paces}
                    />

                    {/* Week Navigation */}
                    <div className="week-navigation">
                      {currentPlan?.weeks?.map((week, idx) => (
                        <button
                          key={idx}
                          className={`week-nav-pill ${idx === selectedWeekIndex ? 'active' : ''} ${idx === currentWeekIndex ? 'current' : ''}`}
                          onClick={() => setSelectedWeekIndex(idx)}
                          title={`Week ${week.week_number || idx + 1}`}
                        >
                          {week.week_number || idx + 1}
                        </button>
                      ))}
                    </div>

                    {/* Workout cards */}
                    <div className="week-workout-row">
                      {displayedWeekData.workouts?.map((workout, idx) => {
                        const workoutDate = new Date(workout.date);
                        const dayAbbr = dayAbbreviations[workoutDate.getDay()].toUpperCase();
                        // Use workout_name if available, otherwise fall back to description or type name
                        const displayName = workout.workout_name || workout.description || getWorkoutTypeName(workout.type);

                        return (
                          <div
                            key={idx}
                            className="workout-day-card"
                          >
                            <span className="card-day">{dayAbbr}</span>
                            <span className="card-type">
                              {displayName}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {displayedWeekData.focus && (
                      <p className="week-summary-focus">
                        {displayedWeekData.focus.replace(/\s*\|\s*🎯\s*FITNESS RETEST WEEK \(Optional\)/gi, ' | Fitness Retest (Optional)')}
                      </p>
                    )}

                    {displayedWeekData.is_retest_week && (
                      <button
                        className="btn-retest-compact"
                        onClick={() => handleOpenRetestModal(displayedWeekData.week_number)}
                      >
                        Fitness Retest
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Calendar View */}
            <CalendarView
              trainingPlan={currentPlan}
              profile={profile}
              onPlanUpdate={handlePlanUpdate}
              onCreatePlan={() => setCreatePlanModalOpen(true)}
              onOpenWorkoutBuilder={(date) => {
                setWorkoutBuilderDate(date || null);
                setWorkoutBuilderOpen(true);
              }}
              refreshTrigger={calendarRefreshTrigger}
            />

            {/* Garmin Connect */}
            <GarminConnect
              trainingPlan={currentPlan}
              onSyncComplete={(data) => console.log('Sync complete:', data)}
            />

            {/* Whoop Connect */}
            <WhoopConnect />

            {/* Oura Ring Connect */}
            <OuraConnect />
          </div>
        </div>
      </div>

      {/* Retest Modal */}
      <RetestModal
        isOpen={retestModalOpen}
        onClose={handleCloseRetestModal}
        retestWeek={selectedRetestWeek}
        currentPlan={currentPlan}
        onPlanUpdate={handlePlanUpdate}
      />

      {/* Create Plan Modal */}
      <CreatePlanModal
        isOpen={createPlanModalOpen}
        onClose={() => setCreatePlanModalOpen(false)}
        profile={profile}
        onPlanCreated={(plan) => {
          setCurrentPlan(plan);
          if (onPlanUpdate) onPlanUpdate(plan);
        }}
      />

      {/* Workout Builder Modal */}
      {workoutBuilderOpen && (
        <WorkoutBuilder
          onClose={() => {
            setWorkoutBuilderOpen(false);
            setWorkoutBuilderDate(null);
          }}
          garminUserId={garminUserId}
          initialDate={workoutBuilderDate}
          onWorkoutCreated={(workout) => {
            console.log('Workout created:', workout);
            // Trigger calendar refresh to fetch the newly saved workout from database
            setCalendarRefreshTrigger(prev => prev + 1);
          }}
        />
      )}
    </div>
  );
};

export default TrainingPlanView;
